/*!
 * vue-bem-styling v0.10.17 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import { curry } from 'ramda';
import { createBlockGenerator, createStateGenerator } from '@aspectus/bem';

var BLOCK_MODIFIERS = {
  theme: 'theme',
  view: 'view',
  styling: 'styling',
  variant: 'variant',
  appearance: 'appearance',
  kind: 'kind',
  align: 'align',
  fit: 'fit'
};
var BLOCK_STATES = {
  disabled: 'disabled',
  passive: 'passive',
  readonly: 'readonly',
  required: 'required',
  invalid: 'invalid',
  active: 'active',
  opened: 'opened',
  hidden: 'hidden'
};

var mapSame = function mapSame() {
  for (var _len = arguments.length, keys = new Array(_len), _key = 0; _key < _len; _key++) {
    keys[_key] = arguments[_key];
  }

  return keys.reduce(function (acc, x) {
    acc[x] = x;
    return acc;
  }, {});
};
var extractProps = curry(function (propsMap, data) {
  return Object.keys(propsMap).reduce(function (acc, x) {
    acc[propsMap[x]] = data[x];
    return acc;
  }, {});
});
var extractChangers = function extractChangers(props) {
  var modifiers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : BLOCK_MODIFIERS;
  var states = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : BLOCK_STATES;
  return [extractProps(modifiers, props), extractProps(states, props)];
};

/* eslint-disable prefer-object-spread */
var MODIFIER_PROP_DEFINITION = {
  type: [String, Array, Boolean, Number]
};
var STATE_PROP_DEFINITION = {
  type: Boolean,
  default: false
};

var samePropReducer = function samePropReducer(prop) {
  return function (acc, x) {
    acc[x] = prop;
    return acc;
  };
};

var constructProps = function constructProps(map, definition) {
  return Object.keys(map).reduce(samePropReducer(definition), {});
};
var MODIFIER_PROPS = constructProps(BLOCK_MODIFIERS, MODIFIER_PROP_DEFINITION);
var STATE_PROPS = constructProps(BLOCK_STATES, STATE_PROP_DEFINITION);
var STYLING_PROPS = Object.assign({}, MODIFIER_PROPS, STATE_PROPS);
var StylingPropsMixin = {
  props: STYLING_PROPS
};
var createStylingFunctionalMixin = function createStylingFunctionalMixin() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$blockConfig = _ref.blockConfig,
      blockConfig = _ref$blockConfig === void 0 ? {} : _ref$blockConfig,
      _ref$stateConfig = _ref.stateConfig,
      stateConfig = _ref$stateConfig === void 0 ? {} : _ref$stateConfig,
      _ref$modifiers = _ref.modifiers,
      modifiers = _ref$modifiers === void 0 ? BLOCK_MODIFIERS : _ref$modifiers,
      _ref$states = _ref.states,
      states = _ref$states === void 0 ? BLOCK_STATES : _ref$states;

  return {
    mixins: [StylingPropsMixin],
    props: {
      blockName: String,
      // Block class generator
      bcg: {
        type: Function,
        default: createBlockGenerator(blockConfig)
      },
      // Block state generator
      bsg: {
        type: Function,
        default: createStateGenerator(stateConfig)
      },
      bclass: {
        type: Function,
        default: function _default() {
          var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
          return [props.bcg(props.blockName)(extractProps(modifiers)(props)), props.bsg(extractProps(states)(props))];
        }
      },
      eclass: {
        type: Function,
        default: function _default(element) {
          var md = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
          var props = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : this;
          return props.bcg(props.blockName)(element, md);
        }
      }
    }
  };
};
var StylingFunctionalMixin = createStylingFunctionalMixin();
var StylingMixin = {
  mixins: [StylingPropsMixin],
  BLOCK_MODIFIERS: BLOCK_MODIFIERS,
  BLOCK_STATES: BLOCK_STATES,
  computed: {
    modifiers: function modifiers() {
      return this.extractModifiers();
    },
    states: function states() {
      return this.extractStates();
    }
  },
  methods: {
    extractModifiers: function extractModifiers() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
      return extractProps(this.$options.BLOCK_MODIFIERS)(props);
    },
    extractStates: function extractStates() {
      var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this;
      return extractProps(this.$options.BLOCK_STATES)(props);
    }
  }
};

export { BLOCK_MODIFIERS, BLOCK_STATES, MODIFIER_PROPS, MODIFIER_PROP_DEFINITION, STATE_PROPS, STATE_PROP_DEFINITION, STYLING_PROPS, StylingFunctionalMixin, StylingMixin, StylingPropsMixin, constructProps, createStylingFunctionalMixin, extractChangers, extractProps, mapSame };
